import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiBell,
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiLogOut,
  FiMenu,
  FiMessageSquare,
  FiSearch,
  FiSettings,
  FiUser,
} from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { logout, selectUser } from '../../store/slices/userSlice';
import socket, { connectAdminSocket, disconnectAdminSocket } from '../../socket';
import axios from '../../utils/axios';
import { resolveApiFileUrl } from '../../utils/fileUrl';
import navigationItems from './navigationItems';
import QuickNavigation from './QuickNavigation';

const detailLabels = {
  '/users': 'User details',
  '/jobs': 'Job details',
  '/applications': 'Application details',
  '/bids': 'Bid details',
  '/subscriptions': 'Subscription details',
  '/support-tickets': 'Ticket conversation',
};

const notificationKey = (notification) => {
  const id = notification?._id || notification?.id;
  if (id) return `id:${id}`;

  return [
    notification?.type,
    notification?.target,
    notification?.message || notification?.title,
    notification?.createdAt || notification?.time,
  ].join('|');
};

const mergeUniqueNotifications = (...groups) => {
  const seen = new Set();

  return groups.flat().filter((notification) => {
    if (!notification || typeof notification !== 'object') return false;
    const key = notificationKey(notification);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const formatNotificationTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const Navbar = ({ onMenuClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector(selectUser);
  const userKey = user?._id || user?.id || user?.email || (user ? 'authenticated' : '');

  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isQuickNavigationOpen, setIsQuickNavigationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');

  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const profileButtonRef = useRef(null);
  const notificationRequestRef = useRef(0);
  const socketNotificationKeysRef = useRef(new Set());

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleShortcut = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      setIsNotificationsOpen(false);
      setIsProfileOpen(false);
      setIsQuickNavigationOpen((open) => !open);
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    setIsNotificationsOpen(false);
    setIsProfileOpen(false);
    setIsQuickNavigationOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!userKey) {
      disconnectAdminSocket();
      return undefined;
    }

    connectAdminSocket();
    const socketNotificationKeys = socketNotificationKeysRef.current;
    const handleNewNotification = (notification) => {
      const key = notificationKey(notification);
      if (socketNotificationKeys.has(key)) return;

      socketNotificationKeys.add(key);
      if (socketNotificationKeys.size > 100) {
        const oldestKey = socketNotificationKeys.values().next().value;
        socketNotificationKeys.delete(oldestKey);
      }

      setNotifications((previous) => mergeUniqueNotifications([notification], previous));
      toast(notification.title || notification.message || 'New notification', { icon: '🔔' });
    };

    socket.on('NEW_NOTIFICATION', handleNewNotification);

    return () => {
      socket.off('NEW_NOTIFICATION', handleNewNotification);
      disconnectAdminSocket();
      socketNotificationKeys.clear();
    };
  }, [userKey]);

  const loadNotifications = useCallback(async () => {
    if (!userKey) return;

    const requestId = notificationRequestRef.current + 1;
    notificationRequestRef.current = requestId;
    setNotificationsLoading(true);
    setNotificationsError('');

    try {
      const response = await axios.get('/notifications/my?limit=10');
      if (notificationRequestRef.current !== requestId) return;

      const fetchedNotifications = response.data?.data?.notifications;
      setNotifications((previous) =>
        mergeUniqueNotifications(previous, Array.isArray(fetchedNotifications) ? fetchedNotifications : []),
      );
    } catch (error) {
      if (notificationRequestRef.current !== requestId) return;
      setNotificationsError(
        error.response?.data?.message || 'Notifications could not be loaded.',
      );
    } finally {
      if (notificationRequestRef.current === requestId) setNotificationsLoading(false);
    }
  }, [userKey]);

  useEffect(() => {
    notificationRequestRef.current += 1;
    setNotifications([]);
    setNotificationsError('');
    setNotificationsLoading(false);

    if (userKey) loadNotifications();

    return () => {
      notificationRequestRef.current += 1;
    };
  }, [loadNotifications, userKey]);

  useEffect(() => {
    if (!isNotificationsOpen && !isProfileOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        isNotificationsOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }

      if (isProfileOpen && profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isNotificationsOpen) notificationButtonRef.current?.focus();
        else if (isProfileOpen) profileButtonRef.current?.focus();
        setIsNotificationsOpen(false);
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isNotificationsOpen, isProfileOpen]);

  const handleLogout = () => {
    toast.success('Logged out successfully! See you soon', {
      duration: 3000,
      position: 'top-center',
      icon: '👋',
    });
    dispatch(logout());
    disconnectAdminSocket();
    localStorage.removeItem('token');
    setIsProfileOpen(false);
    navigate('/login', { replace: true });
  };

  const handleNotificationClick = async (notification) => {
    const id = notification?._id || notification?.id;
    if (!id) {
      toast.error('This notification cannot be opened.');
      return;
    }

    try {
      await axios.patch(`/notifications/${id}/read`, {});

      setNotifications((previous) =>
        previous.map((item) =>
          notificationKey(item) === notificationKey(notification) ? { ...item, read: true } : item,
        ),
      );

      if (notification.type === 'new_message' || notification.type === 'business_contact_started') {
        navigate(`/operations?tab=contacts&contactId=${notification.target}`);
      } else if (notification.type === 'practitioner_available') {
        navigate('/operations?tab=availability');
      } else if (notification.targetModel && notification.target) {
        const routeMap = {
          user: 'users',
          users: 'users',
          booking: 'bookings',
          bookings: 'bookings',
          job: 'jobs',
          jobs: 'jobs',
          application: 'applications',
          applications: 'applications',
          bid: 'bids',
          bids: 'bids',
          subscription: 'subscriptions',
          subscriptions: 'subscriptions',
          review: 'reviews',
          reviews: 'reviews',
          supportticket: 'support-tickets',
          'support-ticket': 'support-tickets',
          'support-tickets': 'support-tickets',
        };
        const route = routeMap[notification.targetModel.toLowerCase()];
        if (route === 'reviews') navigate('/reviews');
        else if (route) navigate(`/${route}/${notification.target}`);
      }

      setIsNotificationsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to open notification');
    }
  };

  const userEmail = user?.email || 'Email unavailable';
  const displayName = user?.fullName || 'Admin';
  const userRole = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : 'Admin';
  const isBookingDetail = pathname.startsWith('/bookings/');
  const activePage = (isBookingDetail ? navigationItems.find((item) => item.path === '/cancellations') : navigationItems.find((item) =>
    item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`),
  )) || { path: '/profile', name: 'Profile', icon: FiUser };
  const isDetailPage = isBookingDetail || Boolean(detailLabels[activePage.path] && pathname !== activePage.path);
  const pageTitle = isBookingDetail ? 'Retained booking details' : isDetailPage ? detailLabels[activePage.path] : activePage.name;
  const PageIcon = activePage.icon;
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join('').toUpperCase() || 'A';
  const shortcutLabel = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ K' : 'Ctrl K';
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneLabel = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(currentTime).find((part) => part.type === 'timeZoneName')?.value || localTimeZone;
  const shortTime = currentTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const fullDate = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const shortDate = currentTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <nav
      aria-label="Admin toolbar"
      className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 shadow-[0_4px_24px_-16px_rgba(15,23,42,0.2)] backdrop-blur-xl"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(251,191,36,0.10),_transparent_55%)]" />
        <div className="absolute bottom-0 left-0 h-px w-2/3 bg-gradient-to-r from-amber-400/70 via-amber-200/50 to-transparent" />
      </div>
      <div className="relative px-3 sm:px-6 lg:px-8">
        <div className="flex h-20 min-w-0 items-center justify-between gap-3 lg:h-24">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Open navigation menu"
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200/80 bg-white text-slate-600 shadow-sm transition-colors hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 lg:hidden"
            >
              <FiMenu className="h-6 w-6" aria-hidden="true" />
            </button>

            <span className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-amber-100/80 text-amber-700 shadow-[0_3px_10px_-5px_rgba(180,83,9,0.3)] sm:flex" aria-hidden="true">
              <PageIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <ol aria-label="Breadcrumb" className="mb-1 flex min-w-0 items-center gap-1.5 text-[10px] font-medium text-slate-500 sm:text-xs">
                <li className="flex-shrink-0">
                  <Link to="/" className="rounded-sm transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">Covrly</Link>
                </li>
                <li aria-hidden="true"><FiChevronRight className="h-3 w-3 text-slate-400" /></li>
                <li className="min-w-0 truncate">
                  {isDetailPage ? (
                    <Link to={activePage.path} className="rounded-sm transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">{activePage.name}</Link>
                  ) : <span>Admin workspace</span>}
                </li>
              </ol>
              <p aria-current="page" className="truncate text-base font-semibold leading-tight tracking-tight text-slate-900 sm:text-xl">
                {pageTitle}
              </p>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2 lg:gap-3">
            <button
              type="button"
              onClick={() => {
                setIsQuickNavigationOpen(true);
                setIsNotificationsOpen(false);
                setIsProfileOpen(false);
              }}
              aria-label="Search admin pages"
              aria-haspopup="dialog"
              aria-expanded={isQuickNavigationOpen}
              aria-keyshortcuts="Meta+K Control+K"
              title={`Search admin pages (${shortcutLabel})`}
              className="group flex h-10 w-10 items-center justify-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/80 text-slate-500 transition-colors hover:border-amber-300 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 xl:w-48 xl:justify-start xl:px-3.5 2xl:w-56"
            >
              <FiSearch className="h-[18px] w-[18px] flex-shrink-0 group-hover:text-amber-700" aria-hidden="true" />
              <span className="hidden flex-1 whitespace-nowrap text-left text-sm xl:block">Jump to a page…</span>
              <kbd className="hidden rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-sans text-[10px] text-slate-400 2xl:block">{shortcutLabel}</kbd>
            </button>
            <div title={`${fullDate} · ${localTimeZone}`} className="hidden border-r border-slate-200 pr-4 text-xs xl:block">
              <span className="flex items-center gap-1.5 font-medium text-slate-700"><FiCalendar className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{shortDate}</span>
              <span className="mt-1 flex items-center gap-1.5 text-slate-500"><FiClock className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{shortTime}<span className="text-[10px] text-slate-400">{timeZoneLabel}</span></span>
            </div>
            <Link
              to="/support-tickets"
              aria-label="Support tickets"
              title="Support tickets"
              className="hidden h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 sm:inline-flex"
            >
              <FiMessageSquare className="h-4 w-4" aria-hidden="true" />
              <span className="hidden xl:inline">Support</span>
            </Link>

            <div className="relative" ref={notificationRef}>
              <button
                ref={notificationButtonRef}
                type="button"
                onClick={() => {
                  setIsNotificationsOpen((open) => !open);
                  setIsProfileOpen(false);
                }}
                aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
                aria-expanded={isNotificationsOpen}
                aria-controls="admin-notifications"
                className={`relative flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${isNotificationsOpen ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200/80 bg-white text-slate-600 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700'}`}
              >
                <FiBell className="h-5 w-5" aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1 text-center text-[10px] font-bold leading-none text-slate-950 ring-2 ring-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {isNotificationsOpen && (
                  <motion.div
                    id="admin-notifications"
                    role="region"
                    aria-label="Notifications"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="fixed left-3 right-3 top-[5.5rem] z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_60px_-15px_rgba(15,23,42,0.25)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-4 sm:w-96"
                  >
                    <div className="border-b border-slate-100 bg-gradient-to-br from-amber-50/70 to-white p-4 sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="font-semibold tracking-tight text-slate-900">Your notifications</h2>
                        <span className="whitespace-nowrap rounded-lg border border-amber-200/60 bg-amber-100/70 px-2.5 py-1 text-xs font-medium text-amber-800">
                          {unreadCount} unread
                        </span>
                      </div>
                    </div>

                    <div className="max-h-[min(24rem,calc(100dvh-9rem))] overflow-y-auto">
                      {notificationsLoading && notifications.length === 0 ? (
                        <p className="p-6 text-center text-sm text-gray-500" role="status">
                          Loading notifications…
                        </p>
                      ) : notificationsError && notifications.length === 0 ? (
                        <div className="p-5 text-center">
                          <p className="text-sm text-red-700" role="alert">{notificationsError}</p>
                          <button
                            type="button"
                            onClick={loadNotifications}
                            className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
                          >
                            Retry
                          </button>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="px-6 py-10 text-center">
                          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400"><FiBell className="h-5 w-5" aria-hidden="true" /></span>
                          <p className="text-sm font-medium text-slate-700">No notifications yet</p>
                          <p className="mt-1 text-xs text-slate-500">New updates will appear here.</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <motion.button
                            type="button"
                            key={notificationKey(notification)}
                            whileHover={{ backgroundColor: 'rgba(245, 158, 11, 0.02)' }}
                            onClick={() => handleNotificationClick(notification)}
                            className={`block w-full border-b border-amber-50 p-4 text-left last:border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 ${
                              !notification.read ? 'bg-amber-50/30' : ''
                            }`}
                          >
                            <span className="flex items-start gap-3">
                              {!notification.read && (
                                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                              )}
                              <span className="min-w-0 flex-1">
                                <span
                                  className={`block break-words text-sm ${
                                    !notification.read ? 'font-semibold text-gray-900' : 'text-gray-600'
                                  }`}
                                >
                                  {notification.message || notification.title || 'Notification'}
                                </span>
                                {(notification.time || notification.createdAt) && (
                                  <span className="mt-1 block text-xs text-gray-400">
                                    {formatNotificationTime(notification.time || notification.createdAt)}
                                  </span>
                                )}
                              </span>
                            </span>
                          </motion.button>
                        ))
                      )}
                    </div>

                    {notificationsError && notifications.length > 0 && (
                      <div className="flex items-center justify-between gap-3 border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
                        <span>Latest notifications may be unavailable.</span>
                        <button type="button" onClick={loadNotifications} className="font-semibold underline">
                          Retry
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative" ref={profileRef}>
              <motion.button
                ref={profileButtonRef}
                type="button"
                onClick={() => {
                  setIsProfileOpen((open) => !open);
                  setIsNotificationsOpen(false);
                }}
                whileHover={{ scale: 1.02 }}
                aria-label={`Open profile menu for ${displayName}`}
                aria-expanded={isProfileOpen}
                aria-controls="admin-profile-menu"
                className={`flex items-center gap-2.5 rounded-xl border p-1 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 lg:pr-3 ${isProfileOpen ? 'border-amber-300 bg-amber-50' : 'border-slate-200/80 bg-white hover:border-amber-200 hover:bg-amber-50/60'}`}
              >
                <span className="relative">
                  <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-slate-900 text-amber-200">
                    {user?.profileImage ? (
                      <img
                        src={resolveApiFileUrl(user.profileImage)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-semibold" aria-hidden="true">{initials}</span>
                    )}
                  </span>
                </span>

                <span className="hidden min-w-0 text-left lg:block">
                  <span className="block max-w-28 truncate text-xs font-semibold text-slate-900 2xl:max-w-36">{displayName}</span>
                  <span className="mt-0.5 block max-w-28 truncate text-[10px] font-medium uppercase tracking-wider text-slate-500">{userRole}</span>
                </span>

                <FiChevronDown
                  aria-hidden="true"
                  className={`hidden h-4 w-4 text-gray-400 transition-transform duration-200 lg:block ${
                    isProfileOpen ? 'rotate-180' : ''
                  }`}
                />
              </motion.button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    id="admin-profile-menu"
                    role="region"
                    aria-label="Profile menu"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="fixed left-3 right-3 top-[5.5rem] z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_60px_-15px_rgba(15,23,42,0.25)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-4 sm:w-72"
                  >
                    <div className="border-b border-slate-100 bg-gradient-to-br from-amber-50/70 to-white p-5">
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-amber-700">Your account</p>
                      <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
                      <p className="mt-0.5 break-all text-xs text-amber-700">{userEmail}</p>
                    </div>

                    <div className="p-2">
                      <Link
                        to="/profile"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center rounded-xl px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                      >
                        <FiUser className="mr-3 h-4 w-4" aria-hidden="true" />
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center rounded-xl px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                      >
                        <FiSettings className="mr-3 h-4 w-4" aria-hidden="true" />
                        Settings
                      </Link>
                      <hr className="my-2 border-amber-100" />
                      <motion.button
                        type="button"
                        whileHover={{ x: 4 }}
                        onClick={handleLogout}
                        className="flex w-full items-center rounded-xl px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      >
                        <FiLogOut className="mr-3 h-4 w-4" aria-hidden="true" />
                        Sign out
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <QuickNavigation open={isQuickNavigationOpen} onClose={() => setIsQuickNavigationOpen(false)} />
    </nav>
  );
};

export { mergeUniqueNotifications, notificationKey };
export default Navbar;
